import * as React from 'react';
import { cn } from '@/lib/utils';

export interface UserAvatarProps {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<UserAvatarProps['size']>, string> = {
  sm: 'w-7 h-7 text-[10px]',
  md: 'w-9 h-9 text-xs',
  lg: 'w-12 h-12 text-sm',
};

function computeInitials(first: string, last: string, email: string): string {
  if (first && last) return (first[0] ?? '') + (last[0] ?? '');
  if (first) return first.slice(0, 2);
  if (last) return last.slice(0, 2);
  if (email) {
    const local = email.split('@')[0] ?? '';
    return local.length >= 2 ? local.slice(0, 2) : (local + '?').slice(0, 2);
  }
  return '??';
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  firstName,
  lastName,
  email,
  avatarUrl,
  size = 'md',
  className,
}) => {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();
  const mail = (email ?? '').trim();
  const initials = computeInitials(first, last, mail).toUpperCase();
  const sizeCls = SIZE_CLASSES[size];

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${first} ${last}`.trim() || mail || 'Avatar'}
        className={cn('rounded-full object-cover shadow-sm', sizeCls, className)}
      />
    );
  }
  return (
    <div
      className={cn(
        'rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white font-semibold flex items-center justify-center shadow-sm shrink-0',
        sizeCls,
        className,
      )}
      aria-label={`${first} ${last}`.trim() || mail}
    >
      {initials}
    </div>
  );
};
